import { API_ORIGIN, resolveApiPath, SITE } from '@/app/constants';
import type { ViduV1GetWechatSignatureReply } from '@/app/types';

interface WechatError {
  errMsg: string;
  [key: string]: unknown;
}

interface WechatShareConfig {
  title: string;
  desc: string;
  link: string;
  imgUrl: string;
  success?: () => void;
  cancel?: () => void;
  fail?: (res: WechatError) => void;
}

interface WechatJssdkConfig {
  debug?: boolean;
  appId: string;
  timestamp: string;
  nonceStr: string;
  signature: string;
  jsApiList: string[];
  openTagList: string[];
}

/**
 * 小程序 WebView 专有能力：只有页面被小程序的 web-view 承载时 jweixin 才会挂上它。
 * 普通 H5 里为 undefined，调用方必须判空（见 navigateToMiniappLiveDestination）。
 */
interface WxMiniProgramSDK {
  navigateBack?: (options?: { delta?: number; fail?: () => void }) => void;
  navigateTo?: (options: { url: string; fail?: () => void }) => void;
  postMessage?: (options: { data: unknown }) => void;
  switchTab: (options: { url: string; fail?: () => void }) => void;
}

interface WxSDK {
  previewImage?: (options: {
    current: string;
    urls: string[];
    success?: () => void;
    fail?: () => void;
    cancel?: () => void;
  }) => void;
  miniProgram?: WxMiniProgramSDK;
  config: (config: WechatJssdkConfig) => void;
  ready: (callback: () => void) => void;
  error: (callback: (res: WechatError) => void) => void;
  updateAppMessageShareData: (config: WechatShareConfig) => void;
  updateTimelineShareData: (config: Omit<WechatShareConfig, 'desc'>) => void;
  onMenuShareTimeline?: (config: Omit<WechatShareConfig, 'desc'>) => void;
  onMenuShareAppMessage?: (config: WechatShareConfig) => void;
}

declare global {
  interface Window {
    wx?: WxSDK;
  }
}

/**
 * 微信内置浏览器（含小程序 web-view）。
 *
 * 判断保存方式要用它，而不是 getRequestPlatform()：后者只认路径和 `?source=`，
 * 公众号里直接打开的 H5 不匹配，会掉进 `<a download>` 分支 —— 而微信屏蔽该下载，
 * 用户点了没任何反应。
 */
export const isWechatBrowser = (
  userAgent = typeof navigator === 'undefined' ? '' : (navigator.userAgent ?? ''),
): boolean => userAgent.toLowerCase().includes('micromessenger');

/**
 * 动态加载微信JSSDK脚本
 */
export const loadWechatScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // 如果已经加载过，直接返回
    if (typeof window !== 'undefined' && window.wx) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('微信JSSDK加载失败'));
    document.head.appendChild(script);
  });
};

/**
 * 获取当前页面URL
 */
export const getCurrentUrl = (): string => {
  if (typeof window === 'undefined') return '';

  let url = window.location.href;

  // 移除hash部分，因为微信签名不包含hash
  url = url.split('#')[0];

  return url;
};

/**
 * 获取微信JSSDK配置参数
 */
export const getWechatConfig = async (retryCount = 0): Promise<ViduV1GetWechatSignatureReply> => {
  try {
    const url = getCurrentUrl();
    const response = await fetch(
      `${API_ORIGIN}${resolveApiPath(`/vidu/v1/share/wechat/signature?url=${encodeURIComponent(url)}`)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
      },
    );

    if (!response.ok) {
      throw new Error(`获取微信配置失败: ${response.status}`);
    }

    const result = await response.json();

    // 验证返回的数据
    if (!result.signature || !result.timestamp || !result.noncestr) {
      throw new Error('微信配置数据不完整');
    }

    return result;
  } catch (error) {
    if (retryCount < 2) {
      console.warn('微信配置获取失败，尝试重试...', error);
      await new Promise((resolve) => setTimeout(resolve, 1000)); // 等待1秒后重试
      return getWechatConfig(retryCount + 1);
    }

    console.error('获取微信JSSDK配置失败:', error);
    throw error;
  }
};

/** wx.config 到 wx.ready 的等待上限。超过就认为签名链路卡住了。 */
const WECHAT_CONFIG_TIMEOUT_MS = 15000;

/**
 * 已完成 config 的 URL 及其就绪 promise。
 *
 * 微信要求签名 URL 与当前页面 URL 完全一致（含 query），而本页 URL 会被
 * markClaimedEditingLiveInUrl / syncEditingModeToUrl 用 replaceState 改写，
 * 所以缓存必须按 URL 记 —— URL 变了要重新签，没变就直接复用。
 */
let wechatReadyState: { url: string; promise: Promise<void> } | null = null;

/** 仅供测试重置模块级缓存。 */
export const resetWechatJssdkReadyCache = () => {
  wechatReadyState = null;
};

/**
 * 确保当前 URL 下 JSSDK 已 config 且 wx.ready 已触发。
 *
 * 同一 URL 重复调用复用同一个 promise：签名请求和 wx.config 都只做一次。
 * 失败不写缓存，下次调用会重试。
 */
export const ensureWechatJssdkReady = (debug = false): Promise<void> => {
  if (typeof window === 'undefined' || SITE === 'global') return Promise.resolve();
  const url = getCurrentUrl();
  if (wechatReadyState?.url === url) return wechatReadyState.promise;

  const promise = configureWechatJssdk(debug).catch((error: unknown) => {
    // 失败的结果不能留在缓存里，否则这个页面再也配不上了。
    if (wechatReadyState?.url === url) wechatReadyState = null;
    throw error;
  });
  wechatReadyState = { url, promise };
  return promise;
};

/** 跑一次完整的 config 流程，并等到 wx.ready / wx.error 有结果为止。 */
const configureWechatJssdk = async (debug: boolean): Promise<void> => {
  await initWechatJssdk(debug);
  const wx = window.wx;
  if (!wx) throw new Error('微信JSSDK未加载');
  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (error) reject(error);
      else resolve();
    };
    const timer = setTimeout(() => finish(new Error('微信JSSDK配置超时')), WECHAT_CONFIG_TIMEOUT_MS);
    wx.ready(() => finish());
    wx.error(() => finish(new Error('微信JSSDK配置失败')));
  });
};

/**
 * 初始化微信JSSDK
 */
export const initWechatJssdk = async (debug = false): Promise<void> => {
  if (typeof window === 'undefined' || SITE === 'global') return;

  try {
    // 1. 加载微信JSSDK
    await loadWechatScript();

    const wx = window.wx;
    if (!wx) {
      throw new Error('微信JSSDK未加载');
    }

    // 2. 获取JSSDK配置
    const config = await getWechatConfig();

    const currentUrl = getCurrentUrl();
    console.info('[WechatSDK] wx.config 调用', {
      appId: 'wxdcc2e452d266c2ff',
      url: currentUrl,
      timestamp: config.timestamp,
      nonceStr: config.noncestr,
      signaturePrefix: config.signature?.slice(0, 8),
      debug,
    });

    wx.config({
      debug,
      appId: 'wxdcc2e452d266c2ff',
      timestamp: config.timestamp ?? '',
      nonceStr: config.noncestr ?? '',
      signature: config.signature ?? '',
      jsApiList: [
        'updateAppMessageShareData',
        'updateTimelineShareData',
        'onMenuShareAppMessage', // 兼容旧版本
        'onMenuShareTimeline', // 兼容旧版本
        'hideMenuItems',
        'showMenuItems',
        'hideAllNonBaseMenuItem',
        'showAllNonBaseMenuItem',
        'closeWindow',
        'previewImage',
      ],
      openTagList: ['wx-open-launch-app'],
    });

    // 监听配置成功事件
    wx.ready(() => {
      console.info('[WechatSDK] wx.config 成功，JSSDK 就绪，openTagList 包含 wx-open-launch-app');
    });

    // 监听配置失败事件
    wx.error((err) => {
      console.error(
        '[WechatSDK] wx.config 失败 — 检查：1) abc.vidu.cn 是否在服务号 JS接口安全域名白名单；2) 签名 URL 是否与当前页面 URL 完全一致',
        err,
      );
    });
  } catch (error) {
    console.error('初始化微信JSSDK失败:', error);
    throw error;
  }
};

export const previewWechatImage = async (url: string, signal: AbortSignal): Promise<void> => {
  if (new URL(url).protocol !== 'https:' || signal.aborted) throw new Error('Image preview unavailable');
  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', cancel);
      if (error) reject(error);
      else resolve();
    };
    const cancel = () => finish(new Error('Image preview cancelled'));
    // 这个超时只覆盖「等 JSSDK 就绪」；预览一旦打开就清掉，让用户慢慢看。
    const timer = setTimeout(() => finish(new Error('Image preview timed out')), WECHAT_CONFIG_TIMEOUT_MS);
    signal.addEventListener('abort', cancel, { once: true });
    // 就绪由进房时的预热完成，这里通常直接命中缓存，不再重新签名和 config。
    void ensureWechatJssdkReady()
      .then(() => {
        if (settled) return;
        const wx = window.wx;
        if (!wx?.previewImage) {
          finish(new Error('Image preview unavailable'));
          return;
        }
        clearTimeout(timer);
        try {
          wx.previewImage({
            current: url,
            urls: [url],
            success: () => finish(),
            fail: () => finish(new Error('Image preview failed')),
            cancel,
          });
        } catch {
          finish(new Error('Image preview failed'));
        }
      })
      .catch(() => finish(new Error('Image preview initialization failed')));
  });
};

/**
 * 配置微信分享
 */
export const configWechatShare = (shareConfig: WechatShareConfig): void => {
  if (typeof window === 'undefined') return;

  const wx = window.wx;
  if (!wx) {
    console.error('微信JSSDK未加载，无法配置分享');
    return;
  }

  // 确保分享链接是完整的URL
  let shareLink = shareConfig.link;
  if (shareLink && !shareLink.startsWith('http')) {
    shareLink = window.location.origin + shareLink;
  }

  // 确保图片URL是完整的URL
  let imgUrl = shareConfig.imgUrl;
  if (imgUrl && !imgUrl.startsWith('http')) {
    imgUrl = window.location.origin + imgUrl;
  }

  const finalConfig = {
    ...shareConfig,
    link: shareLink,
    imgUrl: imgUrl,
  };

  // 使用新版分享接口
  wx.ready(() => {
    try {
      // 分享给朋友
      wx.updateAppMessageShareData({
        title: finalConfig.title,
        desc: finalConfig.desc,
        link: finalConfig.link,
        imgUrl: finalConfig.imgUrl,
        success: () => {
          console.info('分享给朋友配置成功');
          finalConfig.success?.();
        },
        cancel: finalConfig.cancel,
        fail: (error) => {
          console.error('分享给朋友配置失败:', error);
          finalConfig.fail?.(error);
        },
      });

      // 分享到朋友圈
      wx.updateTimelineShareData({
        title: finalConfig.title,
        link: finalConfig.link,
        imgUrl: finalConfig.imgUrl,
        success: () => {
          console.info('分享到朋友圈配置成功');
          finalConfig.success?.();
        },
        cancel: finalConfig.cancel,
        fail: (error) => {
          console.error('分享到朋友圈配置失败:', error);
          finalConfig.fail?.(error);
        },
      });

      // 兼容旧版本分享到好友
      wx.onMenuShareAppMessage?.({
        title: finalConfig.title,
        desc: finalConfig.desc,
        link: finalConfig.link,
        imgUrl: finalConfig.imgUrl,
        success: finalConfig.success,
        cancel: finalConfig.cancel,
        fail: finalConfig.fail,
      });

      // 兼容旧版本分享到朋友圈
      wx.onMenuShareTimeline?.({
        title: finalConfig.title,
        link: finalConfig.link,
        imgUrl: finalConfig.imgUrl,
        success: finalConfig.success,
        cancel: finalConfig.cancel,
        fail: finalConfig.fail,
      });
    } catch (error) {
      console.error('配置微信分享时发生错误:', error);
      finalConfig.fail?.(error as WechatError);
    }
  });
};
